import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useQueryResourceAncestors } from '../../../resource-detail/queries/useQueryResourceAncestors.js';
import { useQueryResourceDetail } from '../../../resources/queries/useQueryResourceDetail.js';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { motion } from 'framer-motion';
import { useQueryResourceShareTokens } from '../../queries/useQueryResourceShareTokens.js';
import { useQueryResourceMemberPermissions } from '../../queries/useQueryResourceMemberPermissions.js';
import { getResourceNamePlaceholder } from '../../../resources/utils/getResourceTitle.js';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { SPAN } from '@fabric-app/ui/components/Typography';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { ContentPublish } from './ContentPublish.js';
import { ContentShare } from './ContentShare.js';
import { Tabs } from '@fabric-app/ui/components/Tabs';
import { useState } from 'react';
import { useQueryResourcePublishDetails } from '../../queries/useQueryResourcePublishDetails.js';
import { styled } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';

const ModalShare = ({ open, onOpenChange, resourceId }) => {
  const { data: resource, isLoading: isLoadingResource } = useQueryResourceDetail(resourceId, {
    enabled: open,
    refetchOnMount: false
  });
  const { isLoading: isLoadingAncestors } = useQueryResourceAncestors(resourceId, {
    enabled: open
  });
  const { isLoading: isLoadingShareTokens } = useQueryResourceShareTokens(resourceId, {
    enabled: open
  });
  const queryMemberPermissions = useQueryResourceMemberPermissions({
    resourceId
  }, {
    enabled: open
  });
  const extension = resource?.extension;
  const resourceName = resource ? resource.name || getResourceNamePlaceholder(resource) : null;
  const isLoading = isLoadingShareTokens || isLoadingResource || queryMemberPermissions.isLoading || isLoadingAncestors;
  const { data: publishDetails } = useQueryResourcePublishDetails(resourceId);
  const [tab, setTab] = useState("share");
  return /* @__PURE__ */ jsx(Modal.Root, {
    open,
    onOpenChange,
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      children: [
        /* @__PURE__ */ jsx(Modal.Overlay, {}),
        /* @__PURE__ */ jsx(Tabs.Root, {
          asChild: true,
          defaultValue: "share",
          onValueChange: (v) => {
            setTab(v);
          },
          value: tab,
          children: /* @__PURE__ */ jsxs(Modal.Content, {
            width: "sm+",
            gap: "none",
            style: {
              overflow: "hidden"
            },
            children: [
              /* @__PURE__ */ jsxs(VisuallyHidden, {
                children: [
                  /* @__PURE__ */ jsxs(DialogTitle, {
                    children: [
                      "Share ",
                      resourceName
                    ]
                  }),
                  /* @__PURE__ */ jsxs(DialogDescription, {
                    children: [
                      "Sharing settings for ",
                      resourceName
                    ]
                  })
                ]
              }),
              isLoading || !resourceId ? /* @__PURE__ */ jsx(Flex, {
                as: motion.div,
                justifyContent: "center",
                alignItems: "center",
                flexGrow: true,
                style: {
                  padding: "4rem 2rem"
                },
                children: /* @__PURE__ */ jsx(Spinner, {
                  size: 24
                })
              }) : /* @__PURE__ */ jsxs(Fragment, {
                children: [
                  /* @__PURE__ */ jsx(Flex, {
                    direction: "column",
                    flexShrink: false,
                    children: /* @__PURE__ */ jsx(Modal.Body, {
                      children: /* @__PURE__ */ jsxs(Flex, {
                        direction: "column",
                        gap: "elementsContainer",
                        children: [
                          /* @__PURE__ */ jsxs(Flex, {
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "elementsContainer",
                            children: [
                              /* @__PURE__ */ jsxs(Title, {
                                style: {
                                  overflow: "hidden"
                                },
                                children: [
                                  /* @__PURE__ */ jsx(SPAN, {
                                    children: "Share"
                                  }),
                                  " ",
                                  resourceName && /* @__PURE__ */ jsxs(Fragment, {
                                    children: [
                                      "‘",
                                      /* @__PURE__ */ jsx(SPAN, {
                                        ellipsis: true,
                                        weight: 400,
                                        as: motion.span,
                                        ...framerAnimationFade,
                                        children: resourceName
                                      }),
                                      extension && /* @__PURE__ */ jsxs(SPAN, {
                                        weight: 400,
                                        color: "placeholder",
                                        children: [
                                          ".",
                                          extension
                                        ]
                                      }),
                                      "’"
                                    ]
                                  })
                                ]
                              }),
                              /* @__PURE__ */ jsx(Modal.Close, {})
                            ]
                          }),
                          resource && resource.kind !== "folder" && /* @__PURE__ */ jsxs(Tabs.List, {
                            style: {
                              flexGrow: 0,
                              width: "max-content"
                            },
                            children: [
                              /* @__PURE__ */ jsx(Tabs.Trigger, {
                                value: "share",
                                children: "Share"
                              }),
                              /* @__PURE__ */ jsxs(Tabs.Trigger, {
                                value: "publish",
                                children: [
                                  !!publishDetails && /* @__PURE__ */ jsx(PublishedDot, {}),
                                  " Publish to web"
                                ]
                              })
                            ]
                          })
                        ]
                      })
                    })
                  }),
                  resource && /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      (tab === "share" || resource.kind === "folder") && /* @__PURE__ */ jsx(ContentShare, {
                        resource
                      }),
                      tab === "publish" && resource.kind !== "folder" && /* @__PURE__ */ jsx(ContentPublish, {
                        resource
                      })
                    ]
                  })
                ]
              })
            ]
          })
        })
      ]
    })
  });
};
const PublishedDot = styled.span.withConfig({
  displayName: "ModalShare__PublishedDot",
  componentId: "sc-4047ac8a-0"
})([
  `background-color:#24cd2b;width:8px;height:8px;border-radius:50%;display:block;`
]);
const Title = styled.div.withConfig({
  displayName: "ModalShare__Title",
  componentId: "sc-4047ac8a-1"
})([
  `font-size:1rem;font-weight:400;color:`,
  `;`
], cssVar["color-text-primary"]);

export { ModalShare };
//# sourceMappingURL=ModalShare.js.map
